import { useEffect, useRef } from "react";

/**
 * Load a brand's fonts, whether they come from Google or from the brand's own upload.
 *
 * ## The problem this shape solves
 *
 * A theme's `font` / `title_font` are plain strings, and each string is used for TWO
 * things: it keys the loader, and it is dropped straight into CSS `font-family`. That is
 * fine while every font is a Google font, because the family name is also the lookup key.
 * An uploaded font breaks it — the thing you must fetch is a URL, and a URL is not a
 * legal `font-family`.
 *
 * Rather than change the theme's shape (which would ripple through the API, every saved
 * theme, and the postMessage the Studio preview speaks), the string is allowed to be
 * EITHER a Google family name or a font-file URL, and two helpers keep the two uses
 * straight:
 *
 *   isCustomFont(value)      — which kind is this?
 *   resolveFontFamily(value) — what do I put in `font-family`?
 *
 * A caller that renders text uses `resolveFontFamily`. A caller that loads uses the hook.
 * Existing themes are untouched: a bare name still takes the Google path exactly as before.
 *
 * ## Why the family name is derived, not stored
 *
 * An uploaded font needs SOME family name for `@font-face` to bind to. Deriving it from
 * the URL keeps the value self-describing — no second field to persist, migrate, or get
 * out of sync with the file. It is also stable, so the same upload always produces the
 * same family and the dedupe below works across components.
 */

/** A value is a custom font when it points at a file rather than naming a family. */
export const isCustomFont = (value: string | undefined): boolean =>
  !!value && (/^https?:\/\//i.test(value) || value.startsWith("/"));

/**
 * A CSS-safe, deterministic family name for an uploaded font.
 *
 * Derived rather than random so two components loading the same URL agree on the family,
 * and so a re-render never invents a new one (which would re-trigger `@font-face` and
 * flash the fallback).
 */
export const customFontFamilyName = (url: string): string => {
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    hash = (hash << 5) - hash + url.charCodeAt(i);
    hash |= 0;
  }
  return `vera-font-${Math.abs(hash).toString(36)}`;
};

/** What to put in CSS `font-family` for a theme value of either kind. */
export const resolveFontFamily = (value: string | undefined): string | undefined =>
  isCustomFont(value) ? customFontFamilyName(value as string) : value;

const googleUrl = (fontName: string): string =>
  `https://fonts.googleapis.com/css2?family=${fontName.replace(
    / /g,
    "+",
  )}:wght@300;400;500;600;700&display=swap`;

/**
 * Inject an uploaded font's `@font-face`, idempotently.
 *
 * Exported because apps/app has its own font loader (`createMuiTheme`'s `loadGoogleFont`)
 * for the certificate itself. That one delegates here rather than repeating the rule, so
 * a custom font cannot work on one surface and not the other — the failure that would
 * matter most, since the Studio preview renders through apps/app.
 */
/**
 * The CSS `format()` hint for an uploaded font, derived from its extension.
 *
 * NOT cosmetic, and not safe to hardcode. Per spec a browser may SKIP a source whose
 * declared format it does not support, so an `.otf` announced as `woff2` can fail to load
 * with nothing in the console explaining it — the text just renders in the fallback. The
 * upload endpoint accepts woff2/otf/ttf (a brand receives whatever its foundry ships), so
 * the hint has to follow the actual file.
 */
const fontFormatHint = (url: string): string => {
  // Strip query/fragment first: a cache-busted or signed URL would otherwise make the
  // extension unrecognisable.
  const path = url.split(/[?#]/)[0].toLowerCase();
  if (path.endsWith(".woff2")) return "woff2";
  if (path.endsWith(".woff")) return "woff";
  if (path.endsWith(".otf")) return "opentype";
  if (path.endsWith(".ttf")) return "truetype";
  // Unknown extension: emit NO hint rather than a wrong one. With no `format()` the
  // browser sniffs the bytes, which is the safer failure mode.
  return "";
};

export const loadCustomFont = (url: string): void => {
  const family = customFontFamilyName(url);
  const id = `vera-font-face-${family}`;
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  const hint = fontFormatHint(url);
  // `font-display: swap` matches the Google path, so a slow upload shows the fallback and
  // then swaps rather than blocking text on the network.
  style.textContent =
    `@font-face{font-family:'${family}';` +
    `src:url('${url}')${hint ? ` format('${hint}')` : ""};font-display:swap;}`;
  document.head.appendChild(style);
};

/**
 * Inject the font once, by whichever mechanism its kind requires.
 *
 * Google fonts get a stylesheet `<link>`; uploads get an `@font-face` rule. Both go on
 * `document.head` and both are deduped by the resource they load.
 *
 * `@font-face` on the document applies INSIDE shadow roots too, which matters because
 * apps/app renders into one.
 *
 * NOTE for the upload path: a font file is subject to CORS in a way an image is not. If
 * the bucket serving it does not send `Access-Control-Allow-Origin`, the browser refuses
 * the font and silently falls back, with nothing in the console naming the theme. Any
 * storage wired to this must set that header.
 */
const loadFont = (value: string, loaded: Set<unknown>): void => {
  if (!value || loaded.has(value)) return;

  if (isCustomFont(value)) {
    loadCustomFont(value);
    loaded.add(value);
    return;
  }

  const href = googleUrl(value);
  if (!document.querySelector(`link[href="${href}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
  loaded.add(value);
};

export const useDynamicFontLoader = (fontName: string | undefined) => {
  const loadedFonts = useRef(new Set());

  useEffect(() => {
    if (!fontName) return;
    loadFont(fontName, loadedFonts.current);
  }, [fontName]);
};

export const useDynamicFontsLoader = (fontNames: string[]) => {
  const loadedFonts = useRef(new Set());

  useEffect(() => {
    fontNames.forEach((fontName) => loadFont(fontName, loadedFonts.current));
  }, [fontNames]);
};
